import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { ListNews } from "../../../components";
import { useMemo, useState } from "react";
import { PostItems } from "../../../types/PostsList";
import { NavigationTypes } from "../../../types/HomeTypes";
import { TagPostsList } from "../../../graphql/tag";
import { FooterWidget, NewMenu } from "../../../types/Menu";
import { itemsMenuHierarch } from "../../../utils/menuHierarch";
import { InView } from "react-intersection-observer";
import { executeQuery } from "../../../services/graphql";
import MainLayout from "../../../components/MainLayout";
import request from "../../../utils/requests";
import { CACHE_TIME_DEFAULT } from "../../../utils/requests";
import { getFooterMenu } from "../../../repositories/menu";
import Loader from "../../../public/static-next/loader.svg";
import { TagsQueryRequest } from "../../../services/graphql/types/tagsQuery";

interface PostsQueryResult {
  menu: Array<NewMenu>;
  edges: Array<PostItems>;
  tagName: string;
  categories: Array<NavigationTypes>;
  slug: string;
  endCursor: string;
  hasNextPage: boolean;
  footerMenu: FooterWidget;
}

const TagPosts: NextPage<PostsQueryResult> = ({
  menu,
  edges,
  tagName,
  categories,
  slug,
  endCursor,
  hasNextPage,
  footerMenu,
}) => {
  const [posts, setPosts] = useState(edges);
  const [queryCursor, setQueryCursor] = useState(endCursor);
  const [nextPage, setNextPage] = useState(hasNextPage);
  const [loading, setLoading] = useState(false);

  const memorizedListData = useMemo(
    () => (
      <ListNews
        edges={posts}
        name={tagName}
        navigation={categories}
        displayDescription={true}
      />
    ),
    [posts]
  );

  return (
    <MainLayout
      customStyle={{ backgroundColor: "#f9f9f9" }}
      head={{ title: `Tag: ${tagName} - Veja todos as postagens` }}
      headerMenu={menu}
      footerMenu={footerMenu}
    >
      {memorizedListData}

      {loading && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Loader style={{ width: 140, height: 140 }} />
        </div>
      )}

      {nextPage && (
        <InView
          as="div"
          onChange={async (inView) => {
            if (inView) {
              setLoading(true);
              const { newPosts, endCursorRefetch, hasNextPage } =
                await request.get(
                  `/api/posts/tags?queryCursor=${queryCursor}&slug=${slug}`
                );
              setPosts([...posts, ...newPosts]);
              setQueryCursor(endCursorRefetch);
              setNextPage(hasNextPage);
              setLoading(false);
            }
          }}
        ></InView>
      )}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params.slug as string;

  const { data } = await executeQuery<TagsQueryRequest>(TagPostsList, {
    first: 36,
    slugQuery: slug,
  });

  const edges = data?.tag?.posts?.edges;
  const tagName = data?.tag?.name;
  const hasNextPage = data?.tag?.posts?.pageInfo.hasNextPage;
  const categories = data?.categories?.nodes;
  const endCursor = data?.tag?.posts?.pageInfo.endCursor;
  const menu = itemsMenuHierarch(data?.Head?.nodes[0]?.menuItems?.nodes);
  const footerMenu = await getFooterMenu();

  if (!data || !edges) {
    return { notFound: true };
  }

  return {
    props: {
      menu,
      edges,
      tagName,
      categories,
      endCursor,
      hasNextPage,
      slug,
      footerMenu,
    },
    revalidate: CACHE_TIME_DEFAULT,
  };
};

export default TagPosts;
